import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Browse Categories');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full h-screen min-h-[700px] flex items-center overflow-hidden">
      {/* Background image with gradient overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20abstract%20composition%20of%20delicate%20fashion%20fabric%20swatches%20in%20cream%20blush%20sand%20and%20sage%20green%20tones%20gently%20layered%20with%20organic%20flowing%20movement%2C%20subtle%20textile%20texture%20details%2C%20dreamy%20diffused%20morning%20light%20creating%20ethereal%20atmosphere%2C%20artistic%20editorial%20backdrop%20with%20plenty%20of%20negative%20space%20and%20gentle%20color%20transitions&width=1800&height=1200&seq=hero-bg-v2-025&orientation=landscape"
          alt="Kakobuy Spreadsheet - Product Resource Background"
          title="Kakobuy Spreadsheet Product Resources"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 lg:gap-0">
          {/* Left side - Main content */}
          <div className="flex flex-col items-start lg:w-[60%]">
            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative mb-8 md:mb-10">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 h-11 px-5 rounded-full border border-white/40 text-white text-sm font-medium bg-transparent hover:bg-white/15 transition-all duration-200 cursor-pointer whitespace-nowrap"
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
              >
                <span>{selectedCategory}</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-base"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-base"></i>
                  )}
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl overflow-hidden z-50 max-h-[420px] overflow-y-auto" role="listbox">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategorySelect(cat)}
                      className="w-full text-left px-5 py-3 text-sm text-foreground-800 hover:bg-accent-100 hover:text-foreground-950 transition-colors duration-150 flex items-center gap-3 cursor-pointer whitespace-nowrap"
                      role="option"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Trust indicator */}
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="flex -space-x-2">
                <div className="w-10 h-10 rounded-full border-2 border-white/80 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20natural%20portrait%20of%20a%20person%20with%20a%20genuine%20relaxed%20smile%20looking%20slightly%20away%20from%20camera%2C%20soft%20golden%20hour%20lighting%2C%20clean%20cream%20colored%20studio%20backdrop%2C%20casual%20modern%20style%20with%20a%20light%20linen%20shirt%2C%20approachable%20and%20trustworthy%20editorial%20headshot&width=80&height=80&seq=avatar-v2-a-026&orientation=squarish"
                    alt="Trusted Shopper"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-white/80 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Closeup%20portrait%20of%20a%20young%20adult%20with%20a%20thoughtful%20confident%20expression%2C%20soft%20diffused%20studio%20light%20creating%20gentle%20shadows%20across%20the%20face%2C%20light%20sand%20colored%20backdrop%2C%20modern%20relaxed%20fashion%20aesthetic%20with%20a%20simple%20knit%20top%2C%20clean%20editorial%20style&width=80&height=80&seq=avatar-v2-b-027&orientation=squarish"
                    alt="Trusted Shopper"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-white/80 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Friendly%20portrait%20of%20a%20person%20with%20bright%20eyes%20and%20a%20subtle%20smile%20facing%20the%20camera%2C%20soft%20natural%20window%20light%2C%20light%20beige%20wall%20background%2C%20relaxed%20casual%20vibe%20with%20a%20neutral%20tone%20t-shirt%2C%20contemporary%20lifestyle%20headshot%20with%20warm%20authentic%20feel&width=80&height=80&seq=avatar-v2-c-028&orientation=squarish"
                    alt="Trusted Shopper"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <span className="text-white/90 text-sm font-medium">Trusted by 10,000+ Shoppers</span>
            </div>

            {/* Title */}
            <h1 className="text-white leading-[0.9] mb-6 md:mb-8">
              <span className="block text-[clamp(3rem,7vw,6.5rem)] font-black tracking-tight">Kakobuy</span>
              <span className="block text-[clamp(2.5rem,6vw,5.5rem)] font-light tracking-tight">Spreadsheet</span>
            </h1>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 h-14 px-8 bg-white text-foreground-950 rounded-full font-semibold text-base hover:bg-accent-500 hover:text-foreground-950 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Kakobuy Spreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 h-14 px-8 border border-white/60 text-white rounded-full font-semibold text-base hover:bg-white/15 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-lg"></i>
                </span>
              </a>
            </div>
          </div>

          {/* Right side - Subtitle */}
          <div className="lg:w-[35%] lg:pl-8">
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-[380px]">
              The Largest Kakobuy Spreadsheet Resource Hub. Find the Best Products, Trusted Sellers, and Updated Buying Guides in One Place.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}